import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';

export default function TeamMemberDialog({ member, onSave, onClose, currentUser }) {
  const [formData, setFormData] = useState(member || {
    name: '',
    email: '',
    phone: '',
    role: 'sales_person',
    branch: currentUser?.branch || '',
    is_active: true
  });

  const [saving, setSaving] = useState(false);

  const roles = [
    { value: 'back_office_manager', label: 'Back Office Manager', backOfficeOnly: true },
    { value: 'customer_care_manager', label: 'Customer Care Manager', backOfficeOnly: true },
    { value: 'account_manager', label: 'Account Manager', backOfficeOnly: true },
    { value: 'loan_officer', label: 'Loan Officer', backOfficeOnly: true },
    { value: 'operations_manager', label: 'Operations Manager', backOfficeOnly: true },
    { value: 'sales_person', label: 'Sales' },
    { value: 'pgvcl_work', label: 'PGVCL Work' },
    { value: 'service_work', label: 'Service Work' },
    { value: 'sales_pgvcl_loan', label: 'Sales/PGVCL/Loan' }
  ];

  const branches = ['Rajkot', 'Jamnagar', 'Porbandar', 'Junagadh', 'Morbi', 'Khambhaliya'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = member?.user_id
        ? `${process.env.REACT_APP_BACKEND_URL}/api/users/${member.user_id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/users`;

      const method = member?.user_id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSave();
        onClose();
      } else {
        alert('Failed to save team member');
      }
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Error saving team member');
    } finally {
      setSaving(false);
    }
  };

  const selectedRole = roles.find(r => r.value === formData.role);
  const isBackOfficeRole = selectedRole?.backOfficeOnly;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {member ? 'Edit Team Member' : 'Add Team Member'}
          </DialogTitle>
          <DialogDescription>
            {member ? 'Update team member information and permissions' : 'Add a new team member to Solar Technology'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="e.g., Komal Madam"
              />
            </div>

            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                placeholder="email@solartechnology.com"
              />
            </div>

            <div>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+91 98765 43210"
              />
            </div>

            <div>
              <Label htmlFor="role">Role *</Label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                className="w-full border rounded-md p-2"
                required
              >
                {roles.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label} {role.backOfficeOnly ? '(Back Office)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {!isBackOfficeRole && (
              <div>
                <Label htmlFor="branch">Branch *</Label>
                <select
                  id="branch"
                  value={formData.branch || ''}
                  onChange={(e) => setFormData({...formData, branch: e.target.value})}
                  className="w-full border rounded-md p-2"
                  required={!isBackOfficeRole}
                  disabled={!!currentUser?.branch}
                >
                  <option value="">Select Branch</option>
                  {branches.map(branch => (
                    <option key={branch} value={branch}>{branch}</option>
                  ))}
                </select>
                {isBackOfficeRole && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Back office roles have access to all branches
                  </p>
                )}
              </div>
            )}

            {isBackOfficeRole && (
              <div className="col-span-2">
                <Badge variant="outline" className="bg-purple-50 text-purple-700">
                  🏢 Back Office - Access to All Branches
                </Badge>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
              className="w-4 h-4"
            />
            <Label htmlFor="is_active" className="cursor-pointer">
              Active (can login and access system)
            </Label>
          </div>

          <div className="bg-muted/50 p-4 rounded-lg">
            <h4 className="font-semibold text-sm mb-2">Permissions Summary</h4>
            <ul className="text-sm space-y-1 text-muted-foreground">
              {isBackOfficeRole ? (
                <>
                  <li>✓ View and manage data from all branches</li>
                  <li>✓ Assign tasks to any team member</li>
                  <li>✓ Bulk import inquiries</li>
                  <li>✓ Generate reports across branches</li>
                </>
              ) : (
                <>
                  <li>✓ View data only from {formData.branch || 'assigned'} branch</li>
                  <li>✓ Assign tasks within same branch</li>
                  <li>✓ Create inquiries and projects</li>
                  <li>✗ Cannot access other branch data</li>
                </>
              )}
            </ul>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : (member ? 'Update Member' : 'Add Member')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
